import React, { useState, useEffect } from "react";
import axios from "axios";
import CreditCardInfo from "./CreditCardInfo"; // Credit card info component
import "./AddPayment.css";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { MdOutlinePayment } from "react-icons/md";
import LoadingOverlay from "../../General/LoadingOverlay"; // Adjust the path if needed
import { useLocation } from "react-router-dom"; // To get URL params

const AddPayment = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search); // Get the query string parameters

  // Extract values from query parameters
  const organizationName = queryParams.get("organizationName");
  const plan = queryParams.get("plan");
  const price = queryParams.get("price");
  const paymentCycle = queryParams.get("paymentCycle");
  const simNumber = queryParams.get("simNumber");
  const hasSim = queryParams.get("hasSim") === "true"; // assuming it's passed as true or false
  const companyName = queryParams.get("companyName");
  const contactEmail = queryParams.get("contactEmail");
  const contactPhoneNumber = queryParams.get("contactPhoneNumber");
  const contactFullName = queryParams.get("contactFullName");

  const [isLoading, setIsLoading] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [showModal, setShowModal] = useState(false); // Control modal visibility
  const [cardDetails, setCardDetails] = useState(null);
  const [paymentError, setPaymentError] = useState(""); // For handling errors
  const [onboardData, setOnboardData] = useState({}); // Define onboardData state here

  // Mapping Prices from URL (if not passed in, use default values)
  const planPrices = {
    Basic: 199,
    Premium: 399,
    Enterprise: 1199,
  };

  const simPrice = 35; // Monthly sim price

  // Calculate Total Price Correctly
  useEffect(() => {
    const basePlanPrice = parseFloat(price) || planPrices[plan] || 0; // Use planPrice from URL or fallback to default plan
    let simCharge = 0;

    // If the user doesn't have a SIM, apply the SIM price
    if (!hasSim) {
      simCharge = paymentCycle === "monthly" ? simPrice : simPrice * 12; // Calculate sim price based on payment cycle
    }

    // If the payment cycle is yearly, apply 10% discount and calculate yearly price
    if (paymentCycle === "yearly") {
      const yearlyPrice = Math.floor(basePlanPrice * 0.9) * 12; // Apply discount to yearly plan
      setTotalAmount(Math.floor(yearlyPrice) + simCharge); // Round down the yearly price
    } else {
      setTotalAmount(Math.floor(basePlanPrice) + simCharge); // Monthly price
    }
  }, [plan, price, paymentCycle, hasSim]); // Add dependencies

  // Construct onboardData once all necessary information is available
  useEffect(() => {
    if (plan && price && paymentCycle) {
      const basePlanPrice = parseFloat(price) || planPrices[plan] || 0;
      const yearlyPrice = Math.floor(basePlanPrice * 0.9) * 12; // Apply discount to yearly plan

      // Calculate price based on payment cycle
      const calculatedPlanPrice = paymentCycle === "yearly" ? yearlyPrice : basePlanPrice;

      const data = {
        plan: plan,
        totalAmount,
        payEach: paymentCycle,
        planPrice: calculatedPlanPrice,
        customerEmail: contactEmail, // Assume customer email from backend or context
        companyInfo: {
          companyName: companyName, // Replace with actual company data
          organizationName: organizationName,
        },
        simInfo: {
          hasSim: hasSim, // Send actual hasSim info
          selectedSimNumber: simNumber || "",
          simNumberEntered: simNumber || "",
        },
      };
      // Update onboardData with the constructed data
      setOnboardData(data);
    }
  }, [plan, price, paymentCycle, totalAmount, organizationName, hasSim, simNumber]);

  const handleModalClose = () => setShowModal(false);
  const handleModalOpen = () => setShowModal(true);

  // Submit Payment and Save Payment Details
  const handleSubmit = async (cardDetails, onboardData) => {
    if (!cardDetails) {
      setPaymentError("Please enter your credit card details");
      return;
    }

    setIsLoading(true);
    setPaymentError(""); // Clear previous errors

    // Prepare data to submit
    const dataToSubmit = {
      plan: plan,
      planPrice: price,
      totalAmount: totalAmount,
      payEach: paymentCycle,
      hasSim: hasSim,
      companyInfo: {
        companyName: companyName, // Replace with actual company data
        organizationName: organizationName,
      },
      organization : organizationName,
      simNumber: simNumber,
      contactInfo: {
        contactEmail: contactEmail, // Replace with actual email
        contactFullName: contactFullName, // Replace with actual name
        contactPhoneNumber: contactPhoneNumber, // Replace with actual phone number
      },
      cardDetails: cardDetails, // Include card details for payment
    };

    try {
      const response = await axios.post(
        `https://gambot.azurewebsites.net/api/Webhooks/AddPaymentData`,
        dataToSubmit
      );

      // Handle the FirestoreResponse returned from the backend
      if (response.data.Status === "Success") {
        setPaymentError("Payment details saved successfully!");
        // Optionally, navigate to the login page or next step
      } else {
        setPaymentError("Payment verification failed");
      }
    } catch (error) {
      console.error("Payment Error:", error);
      setPaymentError("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false); // Stop loading
    }
  };

  return (
    <div className="addpayment-container">
      <LoadingOverlay loading={isLoading}></LoadingOverlay>
      <h2 className="addpayment-container-header">סיכום מסלול</h2>

      <p className="addpayment-container-plan-info">
        <IoIosCheckmarkCircleOutline className="checkmark-icon" />
        <strong>התכנית הנבחרת:</strong> <u>{plan}</u>
      </p>
      <p className="addpayment-container-plan-info">
        <IoIosCheckmarkCircleOutline className="checkmark-icon" />
        <strong>עלות התכנית:</strong>
        <span className="plan-price-info">
          {paymentCycle === "yearly"
            ? Math.floor(planPrices[plan] * 0.9) // Apply 10% discount to yearly price
            : planPrices[plan]}{" "}
          ₪ {"לחודש"}
        </span>
      </p>

      {/* If user doesn't have SIM, display SIM price */}
      {!hasSim && (
        <p className="addpayment-container-sim-price">
          <IoIosCheckmarkCircleOutline className="checkmark-icon" />
          <strong>עלות סים:</strong> <span>{simPrice}₪</span> {paymentCycle === "monthly" ? "לחודש" : "לשנה"}.
        </p>
      )}

      <p className="addpayment-container-total-amount">
        <IoIosCheckmarkCircleOutline className="checkmark-icon" />
        <strong>
          {" "}
          <u>סה"כ לתשלום:</u>
        </strong>{" "}
        {totalAmount}₪ {paymentCycle === "monthly" ? "לחודש" : "לשנה"}.
      </p>

      <p className="addpayment-vat-disclaimer">המחירים המוצגים לא כוללים מע"מ*</p>

      {paymentError && <p className="addpayment-error-message">{paymentError}</p>}

      <button className="addpayment-container-purchase-btn" onClick={handleModalOpen}>
        <span>הזנת פרטי תשלום</span>
        <MdOutlinePayment className="payment-icon" />
      </button>

      {showModal && (
        <CreditCardInfo
          handleClose={handleModalClose}
          handleSubmit={handleSubmit} // Send the function
          onboardData={onboardData} // Pass onboardData
        />
      )}
    </div>
  );
};

export default AddPayment;

  

